// ============================================================================
// useRiskAssessment
// Hook that fetches contact risk metadata and runs the risk detection engine.
// ============================================================================

'use client';

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { authService } from '@/lib/authService';
import {
  assessRisk,
  type RiskAssessmentInput,
  type RiskAssessmentResult,
  type RiskFlag,
} from '@/lib/finance/riskDetection';

interface ContactRiskMetadata {
  contactCreatedAt?: string | null;
  contactUpdatedAt?: string | null;
  contactLastInvoiceDate?: string | null;
  contactInvoiceCount?: number;
  contactIsVerified?: boolean | null;
  contactIsActive?: boolean | null;
  currentPaymentTermsDays?: number | null;
  previousPaymentTermsDays?: number | null;
  paymentTermsChangedAt?: string | null;
  deliveryAddressUpdatedAt?: string | null;
  outstandingDocuments?: Array<{
    document_name: string;
    document_type?: string | null;
    is_expired?: boolean;
    is_verified?: boolean;
  }>;
}

interface UseRiskAssessmentParams {
  contactId?: number | null;
  documentType?: string;
  documentGrandTotal?: number | null;
  hasInventoryItems?: boolean;
  warehouseName?: string | null;
  isSalesDocument?: boolean;
  enabled?: boolean;
}

interface UseRiskAssessmentReturn {
  riskFlags: RiskFlag[];
  assessment: RiskAssessmentResult | null;
  loading: boolean;
  error: string | null;
  acknowledgedFlags: Set<string>;
  acknowledgeFlag: (flagId: string) => void;
  acknowledgeAll: () => void;
  hasUnacknowledgedBlocking: boolean;
  refetch: () => void;
}

export function useRiskAssessment({
  contactId,
  documentType,
  documentGrandTotal,
  hasInventoryItems,
  warehouseName,
  isSalesDocument = true,
  enabled = true,
}: UseRiskAssessmentParams): UseRiskAssessmentReturn {
  const [metadata, setMetadata] = useState<ContactRiskMetadata | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acknowledgedFlags, setAcknowledgedFlags] = useState<Set<string>>(new Set());
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const lastFetchedContactId = useRef<number | null>(null);

  // Fetch contact risk metadata from API
  useEffect(() => {
    if (!enabled || !contactId) {
      setMetadata(null);
      return;
    }

    // Don't refetch if same contact and no manual trigger
    if (lastFetchedContactId.current === contactId && fetchTrigger === 0) return;
    lastFetchedContactId.current = contactId;

    let cancelled = false;
    const fetchMetadata = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = authService.getTokens()?.access_token;
        const response = await fetch(
          `/api/v1/contacts/${contactId}/risk-metadata`,
          {
            cache: 'no-store',
            credentials: 'include',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
        if (!response.ok) {
          throw new Error(`Risk metadata API returned ${response.status}`);
        }
        const data = await response.json();
        if (!cancelled && data.success !== false) {
          setMetadata({
            contactCreatedAt: data.created_at ?? null,
            contactUpdatedAt: data.updated_at ?? null,
            contactLastInvoiceDate: data.last_invoice_date ?? null,
            contactInvoiceCount: data.invoice_count ?? 0,
            contactIsVerified: data.is_verified ?? null,
            contactIsActive: data.is_active ?? null,
            currentPaymentTermsDays: data.payment_terms_days ?? null,
            previousPaymentTermsDays: data.previous_payment_terms_days ?? null,
            paymentTermsChangedAt: data.payment_terms_changed_at ?? null,
            deliveryAddressUpdatedAt: data.delivery_address_updated_at ?? null,
            outstandingDocuments: data.outstanding_documents ?? [],
          });
        }
      } catch (err) {
        if (!cancelled) {
          // Non-blocking — risk detection still works with partial data
          setError(err instanceof Error ? err.message : 'Failed to fetch risk metadata');
          setMetadata(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchMetadata();
    return () => { cancelled = true; };
  }, [contactId, enabled, fetchTrigger]);

  // Run risk assessment with metadata + document context
  const assessment = useMemo<RiskAssessmentResult | null>(() => {
    if (!enabled || !contactId) return null;

    const input: RiskAssessmentInput = {
      contactId,
      contactName: undefined, // Set by caller if available
      contactCreatedAt: metadata?.contactCreatedAt,
      contactUpdatedAt: metadata?.contactUpdatedAt,
      contactLastInvoiceDate: metadata?.contactLastInvoiceDate,
      contactInvoiceCount: metadata?.contactInvoiceCount,
      contactIsVerified: metadata?.contactIsVerified,
      contactIsActive: metadata?.contactIsActive,
      currentPaymentTermsDays: metadata?.currentPaymentTermsDays,
      previousPaymentTermsDays: metadata?.previousPaymentTermsDays,
      paymentTermsChangedAt: metadata?.paymentTermsChangedAt,
      deliveryAddressUpdatedAt: metadata?.deliveryAddressUpdatedAt,
      outstandingDocuments: metadata?.outstandingDocuments,
      documentType,
      documentGrandTotal,
      hasInventoryItems,
      warehouseName,
      isSalesDocument,
    };

    return assessRisk(input);
  }, [
    enabled,
    contactId,
    metadata,
    documentType,
    documentGrandTotal,
    hasInventoryItems,
    warehouseName,
    isSalesDocument,
  ]);

  const acknowledgeFlag = useCallback((flagId: string) => {
    setAcknowledgedFlags((prev) => new Set(prev).add(flagId));
  }, []);

  const acknowledgeAll = useCallback(() => {
    if (!assessment) return;
    setAcknowledgedFlags(new Set(assessment.flags.map((f) => f.id)));
  }, [assessment]);

  const refetch = useCallback(() => {
    setFetchTrigger((n) => n + 1);
  }, []);

  const riskFlags = assessment?.flags ?? [];
  const hasUnacknowledgedBlocking = (assessment?.flags ?? []).some(
    (f) => f.requiresAcknowledgement && !acknowledgedFlags.has(f.id)
  );

  return {
    riskFlags,
    assessment,
    loading,
    error,
    acknowledgedFlags,
    acknowledgeFlag,
    acknowledgeAll,
    hasUnacknowledgedBlocking,
    refetch,
  };
}
